import type { ReceiptDocument } from '@sp/core';
import { translate, type MessageKey } from '../i18n';

/**
 * The printed receipt as HTML, for a Tamil or bilingual store.
 *
 * Fixed-width text cannot line up Tamil: its vowel signs combine with the letter before them, so a
 * name takes fewer columns than it has characters and every amount after it drifts. Here names wrap
 * in their own cell and amounts sit in a right-aligned column, at 58 mm or 80 mm, through the
 * Windows printer driver (which shapes Tamil correctly). Amounts come straight from the document the
 * server built, so the totals are exactly the text receipt's.
 *
 * "both" prints each label as Tamil / English, and each item's Tamil shelf name under its name.
 */
export function HtmlReceipt({ document, paper }: { document: ReceiptDocument; paper: '58' | '80' }) {
  const both = document.language === 'both';
  const label = (key: MessageKey) => {
    const ta = translate('ta', key);
    return both ? `${ta} / ${translate('en', key)}` : ta;
  };
  const totals = document.totals;

  return (
    <div
      className="receipt-sheet receipt-html mx-auto text-black"
      lang="ta"
      style={{ width: paper === '58' ? '58mm' : '80mm', color: '#000', background: '#fff', fontSize: paper === '58' ? '10.5px' : '12px', lineHeight: 1.35, padding: '2mm' }}
      data-paper={paper}
      data-testid="receipt-html"
    >
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontWeight: 700, fontSize: '1.2em' }}>{document.store.name}</p>
        {document.store.address.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {document.store.phone ? <p>{document.store.phone}</p> : null}
        {document.store.gstin ? (
          <p>
            {label('rcpt.gstin')}: {document.store.gstin}
          </p>
        ) : null}
      </div>
      <hr style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
      <Row left={label('rcpt.invoice')} right={document.invoice.number} />
      <Row left={label('rcpt.date')} right={`${document.invoice.dateLabel} ${document.invoice.timeLabel}`} />
      {document.invoice.cashier ? <Row left={label('rcpt.cashier')} right={document.invoice.cashier} /> : null}
      {document.customer?.name ? <Row left={label('rcpt.customer')} right={document.customer.name} /> : null}
      {document.invoice.isReprint ? <p style={{ textAlign: 'center', fontWeight: 700 }}>{label('rcpt.reprint')}</p> : null}
      <hr style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {document.lines.map((line, index) => (
            <tr key={index} style={{ verticalAlign: 'top' }}>
              <td style={{ paddingBottom: '1mm', wordBreak: 'break-word' }}>
                <span style={{ fontWeight: 600 }}>{line.localName && !both ? line.localName : line.name}</span>
                {both && line.localName ? <span style={{ display: 'block' }}>{line.localName}</span> : null}
                <span style={{ display: 'block', fontSize: '0.9em' }}>
                  {line.detail}
                  {line.batchLabel ? ` ${line.batchLabel}` : ''}
                  {line.discountLabel ? ` ${line.discountLabel}` : ''}
                </span>
              </td>
              <td className="tabular" style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: '2mm' }}>
                {line.totalLabel}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
      <Row left={label('rcpt.gross')} right={totals.grossLabel} />
      {totals.discountLabel ? <Row left={label('rcpt.discount')} right={totals.discountLabel} /> : null}
      {totals.taxRemovalLabel ? <Row left={label('rcpt.noTax')} right={totals.taxRemovalLabel} /> : null}
      <Row left={label('rcpt.taxable')} right={totals.taxableLabel} />
      <Row left={label('rcpt.gst')} right={totals.taxBreakupLabel} />
      {totals.roundOffLabel !== '0.00' ? <Row left={label('rcpt.roundOff')} right={totals.roundOffLabel} /> : null}
      <div style={{ fontWeight: 700, fontSize: '1.25em', marginTop: '1mm' }}>
        <Row left={label('rcpt.total')} right={totals.grandTotalLabel} />
      </div>
      {totals.taxExempt ? <p style={{ textAlign: 'center', fontWeight: 700 }}>{label('rcpt.taxExempt')}</p> : null}

      {document.payments.length > 0 ? <hr style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} /> : null}
      {document.payments.map((payment, index) => (
        <Row key={index} left={payment.label + (payment.reference ? ` · ${payment.reference}` : '')} right={payment.amountLabel} />
      ))}
      {document.changeLabel ? <Row left={label('rcpt.change')} right={document.changeLabel} /> : null}

      <hr style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
      <p style={{ textAlign: 'center' }}>{label('rcpt.thanks')}</p>
      {document.footerLines
        .filter((line) => line !== document.thankYouMessage)
        .map((line) => (
          <p key={line} style={{ textAlign: 'center' }}>
            {line}
          </p>
        ))}
    </div>
  );
}

function Row({ left, right }: { left: string; right: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2mm' }}>
      <span style={{ minWidth: 0 }}>{left}</span>
      <span className="tabular" style={{ whiteSpace: 'nowrap' }}>
        {right}
      </span>
    </div>
  );
}
